import { logError } from '@/utils/logger';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, SignOut, Translate, List, Sun, Moon, SidebarSimple, Signature, ArrowsClockwise, BellRinging } from '@phosphor-icons/react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PhotoUpload from '@/components/PhotoUpload';
import SignaturePad from '@/components/SignaturePad';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/components/ThemeProvider';
import NotificationPrefsDialog from '@/components/NotificationPrefsDialog';

const Header = ({ onMenuToggle, onCollapseToggle, collapsed }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [showPrefsDialog, setShowPrefsDialog] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [sigMode, setSigMode] = useState('draw');

  const handleSignatureUploaded = async (url) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/signature`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_url: url }),
      });
      if (res.ok) { setSignatureUrl(url); toast.success('Signature enregistrée'); }
      else toast.error('Erreur');
    } catch { toast.error('Erreur'); }
  };

  useEffect(() => {
    fetchUser();
    fetchNotifications();
    // Souscription push notifications (idempotent)
    import('@/utils/pushNotifications').then((m) => {
      m.ensurePushSubscription().catch(() => {});
    });
  }, []);

  useEffect(() => {
    const ping = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/presence/ping`, { method: 'POST', credentials: 'include' }).catch(() => {});
    ping();
    const interval = setInterval(ping, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        if (data.signature_url) setSignatureUrl(data.signature_url);
      }
    } catch (error) {
      logError('Failed to fetch user:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.filter(n => !n.read));
      }
    } catch (error) {
      logError('Failed to fetch notifications:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
      navigate('/login');
    } catch (error) {
      logError('Logout failed:', error);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header className="h-14 sm:h-16 bg-[#0F172A]/70 backdrop-blur-xl border-b border-white/10 sticky top-0 z-30">
      <div className="h-full px-3 sm:px-6 flex items-center justify-between">
        {/* Left: hamburger menu on mobile, collapse on desktop */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            className="lg:hidden text-[#94A3B8] hover:text-white hover:bg-[#1E293B]"
            data-testid="menu-toggle"
          >
            <List size={24} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCollapseToggle}
            className="hidden lg:flex text-[#94A3B8] hover:text-white hover:bg-[#1E293B]"
            data-testid="sidebar-collapse"
            title={collapsed ? 'Ouvrir le menu' : 'Réduire le menu'}
          >
            <SidebarSimple size={20} weight={collapsed ? 'regular' : 'fill'} />
          </Button>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Refresh */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.location.reload()}
            className="text-[#94A3B8] hover:text-white hover:bg-[#1E293B]"
            data-testid="refresh-button"
            title="Actualiser la plateforme"
          >
            <ArrowsClockwise size={20} weight="duotone" />
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="text-[#94A3B8] hover:text-white hover:bg-[#1E293B]"
            data-testid="theme-toggle"
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
          >
            {theme === 'dark' ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
          </Button>

          {/* Language - hidden on very small screens */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hidden sm:flex text-[#94A3B8] hover:text-white hover:bg-[#1E293B]" data-testid="language-selector">
                <Translate size={20} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0F172A] border-[#1E293B]">
              <DropdownMenuItem onClick={() => i18n.changeLanguage('fr')} className="text-[#F8FAFC] hover:bg-[#1E293B]">Français</DropdownMenuItem>
              <DropdownMenuItem onClick={() => i18n.changeLanguage('en')} className="text-[#F8FAFC] hover:bg-[#1E293B]">English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => i18n.changeLanguage('zh')} className="text-[#F8FAFC] hover:bg-[#1E293B]">中文</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-[#94A3B8] hover:text-white hover:bg-[#1E293B] relative" data-testid="notifications-button">
                <Bell size={20} />
                {notifications.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-[#EF4444] text-white text-xs">
                    {notifications.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 sm:w-80 bg-[#0F172A] border-[#1E293B]">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-[#94A3B8] text-sm">Aucune notification</div>
              ) : (
                notifications.slice(0, 5).map((notif) => (
                  <DropdownMenuItem key={notif.notification_id} className="text-[#F8FAFC] hover:bg-[#1E293B] flex flex-col items-start p-4">
                    <div className="font-medium">{notif.title}</div>
                    <div className="text-sm text-[#94A3B8] mt-1">{notif.message}</div>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 sm:gap-3 hover:bg-[#1E293B] px-2 sm:px-3 py-2 rounded-md transition-colors" data-testid="user-menu-button">
                <Avatar className="h-8 w-8 border border-[#1E293B]">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#2563EB] text-white text-sm">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium text-[#F8FAFC] truncate max-w-[120px]">{user?.name}</div>
                  <div className="text-xs text-[#94A3B8] capitalize">{user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#0F172A] border-[#1E293B]">
              <div className="px-2 py-2">
                <div className="text-sm font-medium text-[#F8FAFC]">{user?.name}</div>
                <div className="text-xs text-[#94A3B8]">{user?.email}</div>
              </div>
              <DropdownMenuSeparator className="bg-[#1E293B]" />
              <DropdownMenuItem onClick={() => setShowSignatureDialog(true)} className="text-[#F8FAFC] hover:bg-[#1E293B] cursor-pointer" data-testid="my-signature-button">
                <Signature size={16} className="mr-2" />
                Ma signature
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowPrefsDialog(true)} className="text-[#F8FAFC] hover:bg-[#1E293B] cursor-pointer" data-testid="notification-prefs-button">
                <BellRinging size={16} className="mr-2" />
                Préférences d'alertes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-[#EF4444] hover:bg-[#1E293B] cursor-pointer" data-testid="logout-button">
                <SignOut size={16} className="mr-2" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Dialog Ma Signature */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-md">
          <DialogHeader><DialogTitle>Ma signature</DialogTitle></DialogHeader>
          <p className="text-sm text-[#94A3B8]">Cette signature sera apposée sur vos rapports PDF, superposée au sceau officiel de l'entreprise.</p>
          {signatureUrl && (
            <div className="flex flex-col items-center gap-1">
              <img src={signatureUrl} alt="Signature" className="max-h-20 bg-white rounded-md p-2" data-testid="signature-preview" />
              <span className="text-[10px] text-[#64748B]">Signature actuelle</span>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant={sigMode === 'draw' ? 'default' : 'outline'} onClick={() => setSigMode('draw')} className={sigMode === 'draw' ? 'flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] text-white' : 'flex-1 border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]'} data-testid="sig-mode-draw">
              ✍️ Signer à l'écran
            </Button>
            <Button variant={sigMode === 'upload' ? 'default' : 'outline'} onClick={() => setSigMode('upload')} className={sigMode === 'upload' ? 'flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] text-white' : 'flex-1 border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]'} data-testid="sig-mode-upload">
              📷 Image
            </Button>
          </div>
          <div className="flex flex-col items-center gap-4 py-2">
            {sigMode === 'draw' ? (
              <SignaturePad onSaved={handleSignatureUploaded} />
            ) : (
              <PhotoUpload currentUrl={signatureUrl} onUploaded={handleSignatureUploaded} size="lg" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <NotificationPrefsDialog open={showPrefsDialog} onOpenChange={setShowPrefsDialog} />
    </header>
  );
};

export default Header;
